import React from 'react';
import '../../styles/MainLanding.css'; // Includes prayer-time-card styles

/**
 * PrayerCardPreview - Shows what the prayer service card will look like before creation/modification
 * This component mirrors the structure of prayer service cards shown in SignUp and MainLanding
 */
const PrayerCardPreview = ({ prayerData, mode = 'preview' }) => {
  // Debug log to check what data we're receiving
  console.log('🕌 PrayerCardPreview received data:', prayerData);
  
  if (!prayerData || typeof prayerData !== 'object') {
    console.warn('PrayerCardPreview: Invalid prayerData:', prayerData);
    return (
      <div className="prayer-time-card" style={{ 
        border: '2px dashed #4682B4',
        position: 'relative',
        opacity: 0.7,
        minWidth: '320px',
        width: '100%',
        maxWidth: '500px'
      }}>
        <div className="prayer-service-header">
          <div className="prayer-service-title">Prayer Service Preview</div>
        </div>
        <div className="prayer-times-list">
          <div className="prayer-time-item">
            <div className="time-display blue">Time TBD</div>
            <div className="service-name">Service details needed</div>
          </div>
        </div>
      </div>
    );
  }

  // Format prayer type for display
  const formatPrayerType = (type) => {
    if (!type || typeof type !== 'string') return 'Prayer Service';
    const typeMap = {
      'shacharit': 'Shacharit (Morning Services)',
      'mincha': 'Mincha (Afternoon Services)', 
      'maariv': 'Maariv (Evening Services)'
    };
    return typeMap[type.toLowerCase()] || `${type.charAt(0).toUpperCase() + type.slice(1)} Service`;
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return 'Time TBD';
    
    // Handle different time formats
    if (typeof timeStr === 'string') {
      // If it's already formatted (e.g., "8:00 AM"), return as-is
      if (timeStr.match(/\d{1,2}:\d{2}\s?(AM|PM|am|pm)/)) {
        return timeStr;
      }
      
      // If it's in 24-hour format (e.g., "20:00"), convert to 12-hour
      if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
      }
    }
    
    return timeStr || 'Time TBD';
  };

  // Generate service name based on data
  const getServiceName = () => {
    if (prayerData.description && typeof prayerData.description === 'string') {
      return prayerData.description;
    }
    if (prayerData.service_name) {
      return prayerData.service_name;
    }
    return 'Main Minyan';
  };

  // Get card class for styling (shacharit-card, mincha-card, maariv-card)
  const getCardClass = () => {
    const prayerType = prayerData.prayer_type?.toLowerCase();
    if (['shacharit', 'mincha', 'maariv'].includes(prayerType)) {
      return `${prayerType}-card`;
    }
    return 'shacharit-card'; // Default
  };

  // Handle multiple times if provided
  const renderPrayerTimes = () => {
    const times = prayerData.times || [prayerData];
    
    if (!Array.isArray(times)) {
      return (
        <div className="prayer-time-item">
          <div className="time-display blue">{formatTime(prayerData.time)}</div>
          <div className="service-name">{getServiceName()}</div>
        </div>
      );
    }

    return times.map((timeEntry, index) => (
      <div key={index} className="prayer-time-item">
        <div className="time-display blue">{formatTime(timeEntry.time || timeEntry)}</div>
        <div className="service-name">
          {timeEntry.description || timeEntry.service_name || getServiceName()}
        </div>
      </div>
    ));
  };

  return (
    <div className={`prayer-time-card ${getCardClass()}`} style={{ 
      border: mode === 'preview' ? '1px solid #e5e7eb' : '1px solid #e5e7eb',
      position: 'relative',
      minWidth: '320px',
      width: '100%',
      maxWidth: '500px',
      alignSelf: 'stretch'
    }}>

      {/* Prayer service header */}
      <div className="prayer-service-header">
        <div className="prayer-service-title">
          {formatPrayerType(prayerData.prayer_type)}
        </div>
      </div>

      {/* Prayer times list */}
      <div className="prayer-times-list">
        {renderPrayerTimes()}
      </div>

      {/* Day of week indicator if provided */}
      {prayerData.day_of_week && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #f3f4f6',
          fontSize: '12px',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          {Array.isArray(prayerData.day_of_week) 
            ? prayerData.day_of_week.join(', ') 
            : prayerData.day_of_week}
        </div>
      )}

      {/* Additional services if provided */}
      {prayerData.services && (
        <div style={{
          marginTop: '8px',
          fontSize: '12px',
          color: '#6b7280',
          fontStyle: 'italic'
        }}>
          Services: {Array.isArray(prayerData.services) 
            ? prayerData.services.join(', ') 
            : prayerData.services}
        </div>
      )}
    </div>
  );
};

export default PrayerCardPreview;